import { EASE_DRAWER } from '@/lib/motion-utils'

type ApplyTheme = () => void

/** Duration (ms) of the circular theme reveal. Exported so /dev/motion tunes it. */
export const THEME_REVEAL_MS = 450

/** Minimal shape of a React/DOM click event we need to derive the origin. */
type ClickLike = {
  clientX: number
  clientY: number
  detail: number
  currentTarget: Element
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => { ready: Promise<void> }
}

/**
 * Resolve the point the reveal should expand from. Pointer clicks carry real
 * coordinates; keyboard-activated clicks (Enter/Space) report clientX/clientY of
 * 0 with `detail === 0`, so anchor to the control's center instead of the
 * top-left corner.
 */
function resolveOrigin(event: ClickLike): { x: number; y: number } {
  const isKeyboardActivation =
    event.detail === 0 && event.clientX === 0 && event.clientY === 0

  if (isKeyboardActivation) {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
  }

  return { x: event.clientX, y: event.clientY }
}

/**
 * Apply a theme change with a circular clip-path reveal anchored to where the
 * user activated the toggle, using the View Transitions API. The new theme
 * wipes in as an expanding circle instead of swapping instantly.
 *
 * Falls back to an instant apply when the API isn't supported. Intentionally
 * NOT gated on prefers-reduced-motion — animations are shown to everyone by
 * design (see MotionConfig in providers.tsx).
 */
export function startThemeTransition(apply: ApplyTheme, event: ClickLike) {
  if (typeof document === 'undefined') {
    apply()
    return
  }

  const doc = document as DocumentWithViewTransition

  if (!doc.startViewTransition) {
    apply()
    return
  }

  const { x, y } = resolveOrigin(event)

  // Radius to the farthest corner so the circle fully covers the viewport.
  const endRadius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  )

  const transition = doc.startViewTransition(apply)

  transition.ready
    .then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: THEME_REVEAL_MS,
          easing: EASE_DRAWER,
          pseudoElement: '::view-transition-new(root)',
        },
      )
    })
    .catch(() => {
      /* transition interrupted — the theme has already been applied */
    })
}
